// src/components/SupportRequests/SupportRequestsList.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CoreTable from '../../coreComponents/Table/Table.tsx';
import { getSupportRequests } from '../../services/SupportService.ts';
import SupportRequestModal from './SupportRequestDetails.tsx';
import { Box, Typography, Chip } from '@mui/material';
import { SupportRequest } from './types';

// Status chip component for visual status representation
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const statusConfig: Record<string, { color: 'primary' | 'default' | 'warning' | 'info' | 'error'; label: string }> = {
        'open': { color: 'primary', label: 'Open' },
        'closed': { color: 'default', label: 'Closed' },
        'waiting_for_customer': { color: 'warning', label: 'Waiting for Customer' },
        'in_progress': { color: 'info', label: 'In Progress' }
    };

    const config = statusConfig[status] || { color: 'default', label: status };

    return <Chip label={config.label} color={config.color} size="small" />;
};

const columns = [
    { id: 'id', label: 'ID', searchable: false },
    { id: 'subject', label: 'Subject', searchable: true },
    { id: 'userType', label: 'User Type', searchable: false },
    {
        id: 'userId',
        label: 'User',
        searchable: true,
        render: (_: any, row: SupportRequest) =>
            `${row.userType === 'user' ? 'Customer' : 'Driver'} #${row.userId || row.driverId}`
    },
    {
        id: 'created_at',
        label: 'Created',
        searchable: false,
        render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
        id: 'updated_at',
        label: 'Last Updated',
        searchable: false,
        render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
        id: 'status',
        label: 'Status',
        searchable: true,
        render: (value: string) => <StatusChip status={value} />
    },
    {
        id: 'unread',
        label: 'Unread',
        searchable: false,
        render: (_: any, row: SupportRequest) => {
            // Messages sent by user that admin hasn't read
            const unreadByAdmin = row.conversations?.filter(c =>
                c.senderType === 'user' && !c.isReadByAdmin
            ).length || 0;

            // Messages sent by admin that user hasn't read
            const unreadByUser = row.conversations?.filter(c =>
                c.senderType === 'admin' && !c.isReadByUser
            ).length || 0;

            return (
                <Box>
                    {unreadByAdmin > 0 && (
                        <Chip
                            label={`${unreadByAdmin} new`}
                            color="error"
                            size="small"
                            sx={{ mr: 0.5 }}
                        />
                    )}
                    {unreadByUser > 0 && (
                        <Chip
                            label={`${unreadByUser} unread by user`}
                            color="info"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            );
        }
    }
];

const PAGE_SIZE = 10; // Number of items per page

const SupportRequests: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<SupportRequest[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const fetchSupportRequests = async (pageNum: number, refresh = false) => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            const response = await getSupportRequests(pageNum, PAGE_SIZE);

            if (response?.data?.data) {
                const newData = response.data.data;
                setTotalCount(response.data.meta.total);

                if (pageNum === 1 || refresh) {
                    setData(newData);
                } else {
                    setData(prev => [...prev, ...newData]);
                }

                setHasMore(newData.length === PAGE_SIZE && (refresh ? newData.length : data.length + newData.length) < response.data.meta.total);
            }
        } catch (err: any) {
            if (err.response && err.response.status === 401) {
                console.error("Unauthorized access. Redirecting to login...");
                navigate("/login");
            } else {
                setError("Failed to load support requests");
                console.error(err);
            }
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSupportRequests(1, true);
    }, []);

    const loadMore = () => {
        if (!isLoading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchSupportRequests(nextPage);
        }
    };

    const handleViewDetails = (id: number) => {
        const request = data.find(req => req.id === id);
        if (request) {
            setSelectedRequest(request);
            setModalOpen(true);
        }
    };

    const handleModalClose = (refreshData = false) => {
        setModalOpen(false);
        setSelectedRequest(null);
        if (refreshData) {
            // Reset to page 1 and refresh data
            setPage(1);
            fetchSupportRequests(1, true);
        }
    };

    if (loading && data.length === 0) return <div>Loading support requests...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Box>
        

            <CoreTable
                columns={columns}
                data={data}
                onUpdate={handleViewDetails}
                label1="View Details"
                infiniteScroll={true}
                hasMore={hasMore}
                loadMore={loadMore}
                totalCount={totalCount}
                showSearch={false}
            />

            {selectedRequest && (
                <SupportRequestModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    request={selectedRequest}
                />
            )}
        </Box>
    );
};

export default SupportRequests;